/**
 * ============================================================================
 * BulkUploadForm — folder / multi-file document upload (E-4).
 *
 * Owner: E enhancements track.
 *
 * Lets a user drop MANY files (or a whole folder via webkitdirectory) and
 * create one DRAFT document per file in a single action. It loops the existing
 * POST /api/v1/documents/upload — so every file gets the same machinery as a
 * single upload: a configurable doc number (C-12), its artefact persisted to
 * MinIO (E-2), and background OCR incl. Arabic (E-3). A shared department /
 * doc-type / control-profile / language applies to all files; each file's
 * title defaults to its filename. Per-file status is shown as the loop runs.
 * ============================================================================
 */

import { useEffect, useMemo, useRef, useState, type FormEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../api/client';
import { useDepartments } from '../../api/orgHooks';
import type { CreateDocumentResponse } from '../../api/hooks';
import { DOC_TYPE_CODES } from './NewDocumentForm';
import styles from './NewDocumentForm.module.css';

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024; // 8 MB per file (matches single upload)
const ACCEPT =
  '.pdf,.docx,.doc,.odt,.png,.jpg,.jpeg,.tif,.tiff,.txt,' +
  'application/pdf,image/*,text/plain,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const OK_EXT = /\.(pdf|docx?|odt|png|jpe?g|tiff?|txt)$/i;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result as string;
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

type RowStatus = 'pending' | 'uploading' | 'done' | 'error' | 'skipped';
interface Row {
  file?: File;
  name: string;
  size: number;
  type: string;
  status: RowStatus;
  docNumber?: string;
  error?: string;
}

function titleFromFilename(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
  if (base.length >= 3) return base.slice(0, 200);
  return `Upload ${name}`.slice(0, 200);
}

export interface BulkUploadFormProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the batch finishes so the caller can refresh its list. */
  onDone: () => void;
}

export function BulkUploadForm({ isOpen, onClose, onDone }: BulkUploadFormProps) {
  const { t } = useTranslation();
  const firstFieldRef = useRef<HTMLSelectElement>(null);

  const [docTypeCode, setDocTypeCode] = useState('SOP');
  const [departmentId, setDepartmentId] = useState('');
  const [controlProfile, setControlProfile] = useState('GMP_CONTROLLED');
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState('ar+en');
  const [reviewPeriodMonths, setReviewPeriodMonths] = useState('24');
  const [folderMode, setFolderMode] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const depsQ = useDepartments();

  const departmentOptions = useMemo(() => {
    const all = depsQ.data?.items ?? [];
    const byParent = new Map<string | null, typeof all>();
    for (const d of all) {
      const k = d.parentId ?? null;
      if (!byParent.has(k)) byParent.set(k, [] as typeof all);
      byParent.get(k)!.push(d);
    }
    const out: { id: string; label: string }[] = [];
    function walk(parent: string | null, depth: number) {
      const kids = (byParent.get(parent) ?? []).filter((d) => d.isActive);
      kids.sort((a, b) => a.name.localeCompare(b.name));
      for (const d of kids) {
        out.push({ id: d.departmentId, label: '—'.repeat(depth) + ' ' + d.name });
        walk(d.departmentId, depth + 1);
      }
    }
    walk(null, 0);
    return out;
  }, [depsQ.data]);

  useEffect(() => {
    if (!isOpen) return;
    setDocTypeCode('SOP');
    setDepartmentId('');
    setControlProfile('GMP_CONTROLLED');
    setCategory('');
    setLanguage('ar+en');
    setReviewPeriodMonths('24');
    setFolderMode(false);
    setRows([]);
    setSubmitting(false);
    setFinished(false);
    setServerError(null);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) { e.preventDefault(); onClose(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  function onPickFiles(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const next: Row[] = picked.map((f) => {
      if (!OK_EXT.test(f.name)) {
        return { name: f.name, size: f.size, type: f.type, status: 'skipped', error: t('bulkUpload.skipType', { defaultValue: 'unsupported type' }) };
      }
      if (f.size > MAX_UPLOAD_BYTES) {
        return { name: f.name, size: f.size, type: f.type, status: 'skipped', error: t('bulkUpload.skipSize', { defaultValue: '> 8 MB' }) };
      }
      return { file: f, name: f.name, size: f.size, type: f.type, status: 'pending' };
    });
    setRows(next);
    setFinished(false);
    setServerError(null);
  }

  const acceptedCount = rows.filter((r) => r.status !== 'skipped').length;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    if (!departmentId) {
      setServerError(t('bulkUpload.errors.deptRequired', { defaultValue: 'Department is required (department-routed review).' }));
      return;
    }
    if (acceptedCount === 0) {
      setServerError(t('bulkUpload.errors.noFiles', { defaultValue: 'Add at least one supported file (≤ 8 MB each).' }));
      return;
    }
    setSubmitting(true);
    // Mutable copy we patch per row as the loop runs.
    const work = rows.slice();
    for (let i = 0; i < work.length; i += 1) {
      const row = work[i];
      if (!row || row.status === 'skipped' || !row.file) continue;
      work[i] = { ...row, status: 'uploading' };
      setRows(work.slice());
      try {
        const artifactBase64 = await fileToBase64(row.file);
        const resp = await apiFetch<CreateDocumentResponse>('/api/v1/documents/upload', {
          method: 'POST',
          body: {
            title: titleFromFilename(row.name),
            docTypeCode,
            departmentId,
            language,
            category: category || undefined,
            documentClass: controlProfile === 'GMP_CONTROLLED' ? (category || undefined) : undefined,
            controlProfile,
            reviewPeriodMonths: reviewPeriodMonths || undefined,
            artifactBase64,
            artifactMime: row.file.type || 'application/octet-stream',
            artifactFilename: row.name,
          },
        });
        work[i] = { ...row, status: 'done', docNumber: resp.doc_number ?? (resp as { docNumber?: string }).docNumber };
      } catch (err) {
        work[i] = { ...row, status: 'error', error: err instanceof Error ? err.message : String(err) };
      }
      setRows(work.slice());
    }
    setSubmitting(false);
    setFinished(true);
    onDone();
  }

  if (!isOpen) return null;

  const doneCount = rows.filter((r) => r.status === 'done').length;
  const errCount = rows.filter((r) => r.status === 'error').length;
  const skipCount = rows.filter((r) => r.status === 'skipped').length;

  const badge = (s: RowStatus) => {
    const map: Record<RowStatus, { txt: string; color: string }> = {
      pending: { txt: t('bulkUpload.statusPending', { defaultValue: 'queued' }), color: '#6B7989' },
      uploading: { txt: t('bulkUpload.statusUploading', { defaultValue: 'uploading…' }), color: '#0065FF' },
      done: { txt: t('bulkUpload.statusDone', { defaultValue: 'created' }), color: '#16A34A' },
      error: { txt: t('bulkUpload.statusError', { defaultValue: 'failed' }), color: '#DC2626' },
      skipped: { txt: t('bulkUpload.statusSkipped', { defaultValue: 'skipped' }), color: '#D97706' },
    };
    const m = map[s];
    return <span style={{ color: m.color, fontWeight: 600, fontSize: 12 }}>{m.txt}</span>;
  };

  return (
    <div className={styles.backdrop} onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }} role="presentation">
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="bulk-upload-title" aria-describedby="bulk-upload-desc">
        <header className={styles.header}>
          <h2 id="bulk-upload-title" className={styles.title}>{t('bulkUpload.title', { defaultValue: 'Bulk upload documents' })}</h2>
          <p id="bulk-upload-desc" className={styles.desc}>
            {t('bulkUpload.subtitle', { defaultValue: 'Upload many files (or a whole folder) at once — each becomes its own draft, stored and auto-OCR’d. A shared department and type apply to all; titles default to the filename.' })}
          </p>
        </header>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {serverError && <div role="alert" className={styles.errorBanner}>{serverError}</div>}

          <div className={styles.field}>
            <label htmlFor="bulk_doc_type" className={styles.label}>
              {t('newDocument.docTypeLabel')}<span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <select id="bulk_doc_type" ref={firstFieldRef} value={docTypeCode} onChange={(e) => setDocTypeCode(e.target.value)} className={styles.select} disabled={submitting}>
              {DOC_TYPE_CODES.map((dt) => (
                <option key={dt.code} value={dt.code}>{t(dt.labelKey)} ({dt.code})</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk_control_profile" className={styles.label}>
              {t('newDocument.controlProfileLabel', { defaultValue: 'Control profile' })}
            </label>
            <select id="bulk_control_profile" value={controlProfile} onChange={(e) => { setControlProfile(e.target.value); setCategory(''); }} className={styles.select} disabled={submitting}>
              <option value="GMP_CONTROLLED">{t('newDocument.profileGmp', { defaultValue: 'GMP-controlled (full Part-11 rigor)' })}</option>
              <option value="GENERAL_CONTROLLED">{t('newDocument.profileGeneral', { defaultValue: 'General-controlled (non-GMP — lighter)' })}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk_category" className={styles.label}>
              {controlProfile === 'GENERAL_CONTROLLED'
                ? t('newDocument.corporateCategoryLabel', { defaultValue: 'Corporate category' })
                : t('newDocument.categoryLabel', { defaultValue: 'GMP category (classification level)' })}
            </label>
            <select id="bulk_category" value={category} onChange={(e) => setCategory(e.target.value)} className={styles.select} disabled={submitting}>
              <option value="">{t('newDocument.categoryNone', { defaultValue: '— Select —' })}</option>
              {controlProfile === 'GENERAL_CONTROLLED' ? (
                <>
                  <option value="HR">Human Resources</option>
                  <option value="FINANCE">Finance</option>
                  <option value="PROCUREMENT">Procurement</option>
                  <option value="LEGAL">Legal &amp; Compliance</option>
                  <option value="IT">IT &amp; Systems</option>
                  <option value="EHS">Environment, Health &amp; Safety</option>
                  <option value="FACILITIES">Facilities &amp; Admin</option>
                  <option value="GENERAL">General / Corporate</option>
                </>
              ) : (
                <>
                  <option value="QUALITY_MANUAL">Quality Manual (L1)</option>
                  <option value="POLICY">Policy (L2)</option>
                  <option value="SOP">SOP (L3)</option>
                  <option value="WORK_INSTRUCTION">Work Instruction (L4)</option>
                  <option value="FORM">Form (L5)</option>
                  <option value="RECORD">Record (L6)</option>
                </>
              )}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk_language" className={styles.label}>
              {t('newDocument.languageLabel', { defaultValue: 'Language (drives OCR)' })}
            </label>
            <select id="bulk_language" value={language} onChange={(e) => setLanguage(e.target.value)} className={styles.select} disabled={submitting}>
              <option value="ar+en">{t('bulkUpload.langAuto', { defaultValue: 'Auto (Arabic + English)' })}</option>
              <option value="en">{t('newDocument.langEn', { defaultValue: 'English' })}</option>
              <option value="ar">{t('newDocument.langAr', { defaultValue: 'Arabic' })}</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk_department" className={styles.label}>
              {t('newDocument.departmentLabel', { defaultValue: 'Department' })}<span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <select id="bulk_department" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className={styles.select} aria-invalid={!departmentId ? 'true' : 'false'} disabled={submitting}>
              <option value="">{t('newDocument.departmentPlaceholder', { defaultValue: '— Pick a department —' })}</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={folderMode} onChange={(e) => { setFolderMode(e.target.checked); setRows([]); }} disabled={submitting} />
              {t('bulkUpload.folderToggle', { defaultValue: 'Upload an entire folder (includes sub-folders)' })}
            </label>
          </div>

          <div className={styles.field}>
            <label htmlFor="bulk_files" className={styles.label}>
              {folderMode
                ? t('bulkUpload.folderLabel', { defaultValue: 'Choose a folder' })
                : t('bulkUpload.filesLabel', { defaultValue: 'Choose files (PDF / image / DOCX / text, ≤ 8 MB each)' })}
              <span aria-hidden="true" className={styles.required}>*</span>
            </label>
            <input
              id="bulk_files"
              key={folderMode ? 'dir' : 'files'}
              type="file"
              multiple
              accept={folderMode ? undefined : ACCEPT}
              ref={(el) => {
                if (!el) return;
                if (folderMode) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); }
                else { el.removeAttribute('webkitdirectory'); el.removeAttribute('directory'); }
              }}
              onChange={onPickFiles}
              className={styles.input}
              disabled={submitting}
            />
            {rows.length > 0 && (
              <div className={styles.helperText} aria-live="polite">
                {t('bulkUpload.summary', {
                  defaultValue: '{{accepted}} ready · {{skipped}} skipped',
                  accepted: acceptedCount,
                  skipped: skipCount,
                })}
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className={styles.field}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border, #E2E8F0)', borderRadius: 8 }}>
                {rows.map((r, i) => (
                  <li key={`${r.name}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 10px', borderBottom: i < rows.length - 1 ? '1px solid var(--color-border, #EEF2F6)' : 'none' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name}>
                      {r.name} <span style={{ color: '#6B7989', fontSize: 12 }}>· {Math.max(1, Math.round(r.size / 1024))} KB</span>
                    </span>
                    <span style={{ flexShrink: 0 }}>
                      {r.status === 'done' && r.docNumber ? <span style={{ color: '#16A34A', fontWeight: 600, fontSize: 12 }}>{r.docNumber}</span> : badge(r.status)}
                      {r.status === 'error' && r.error ? <span style={{ color: '#DC2626', fontSize: 11, marginInlineStart: 6 }}>{r.error}</span> : null}
                      {r.status === 'skipped' && r.error ? <span style={{ color: '#D97706', fontSize: 11, marginInlineStart: 6 }}>{r.error}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {finished && (
            <div role="status" className={styles.helperText}>
              {t('bulkUpload.finished', {
                defaultValue: 'Done — {{done}} created, {{err}} failed, {{skip}} skipped.',
                done: doneCount, err: errCount, skip: skipCount,
              })}
            </div>
          )}

          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {finished ? t('common.close', { defaultValue: 'Close' }) : t('common.cancel')}
            </Button>
            {!finished && (
              <Button type="submit" variant="primary" isLoading={submitting} disabled={acceptedCount === 0 || !departmentId}>
                {t('bulkUpload.submit', { defaultValue: 'Upload {{n}} files', n: acceptedCount })}
              </Button>
            )}
          </footer>
        </form>
      </div>
    </div>
  );
}
